import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  setDoc,
} from "firebase/firestore";

import {
  FaSearch,
  FaUser,
  FaShieldAlt,
  FaWallet,
  FaTrash,
  FaSave,
  FaEdit,
  FaTimes,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaSpinner,
  FaLock,
  FaChevronRight,
  FaPhone,
  FaMobileAlt,
} from "react-icons/fa";

import {
  db,
} from "../firebase";

import "./AdminSecurityWithdraw.css";


/* =========================================================
   ADMIN SECURITY + PHONE WALLET
========================================================= */

export default function AdminSecurity() {

  /* =======================================================
     USERS
  ======================================================= */

  const [users, setUsers] = useState([]);


  /* =======================================================
     SELECTED USER
  ======================================================= */

  const [selectedUser, setSelectedUser] =
    useState(null);


  /* =======================================================
     SEARCH
  ======================================================= */

  const [search, setSearch] =
    useState("");


  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] =
    useState(true);


  /* =======================================================
     SAVING
  ======================================================= */

  const [saving, setSaving] =
    useState(false);


  /* =======================================================
     MESSAGE
  ======================================================= */

  const [message, setMessage] =
    useState("");


  /* =======================================================
     ERROR
  ======================================================= */

  const [error, setError] =
    useState("");


  /* =======================================================
     EDIT MODE
  ======================================================= */

  const [editMode, setEditMode] =
    useState(false);


  /* =======================================================
     FORM
  ======================================================= */

  const [form, setForm] =
    useState({});


  /* =======================================================
     SHOW PASSWORD
  ======================================================= */

  const [showPassword, setShowPassword] =
    useState(false);


  /* =======================================================
     SHOW SECURITY ANSWER
  ======================================================= */

  const [showAnswer, setShowAnswer] =
    useState(false);


  /* =======================================================
     WALLET EDIT
  ======================================================= */

  const [editingWalletIndex, setEditingWalletIndex] =
    useState(null);


  /* =======================================================
     WALLET FORM
  ======================================================= */

  const [walletForm, setWalletForm] =
    useState({});


  /* =======================================================
     PHONE WALLET FORM
  ======================================================= */

  const [phoneWalletForm, setPhoneWalletForm] =
    useState({});


  /* =======================================================
     PHONE WALLET EDIT MODE
  ======================================================= */

  const [phoneWalletEditMode, setPhoneWalletEditMode] =
    useState(false);


  /* =======================================================
     DELETE CONFIRMATION
  ======================================================= */

  const [deleteConfirm, setDeleteConfirm] =
    useState(false);


  /* =========================================================
     MASK PHONE NUMBER
  ========================================================= */

  const maskPhone = (value) => {

    const phone =
      String(value || "").trim();

    if (!phone) {
      return "Not available";
    }

    if (phone.length <= 4) {
      return "••••";
    }

    return (
      "•••• " +
      phone.slice(-4)
    );

  };


  /* =========================================================
     LOAD USERS
     
     SECURITY COLLECTION
  ========================================================= */

  const loadUsers = async () => {

    try {

      setLoading(true);

      setError("");

      setMessage("");


      const securityCollection =
        collection(
          db,
          "security"
        );


      const snapshot =
        await getDocs(
          securityCollection
        );


      const loadedUsers =
        snapshot.docs.map(
          (item) => ({
            id: item.id,
            ...item.data(),
          })
        );


      setUsers(
        loadedUsers
      );


    } catch (err) {

      console.error(
        "Admin security loading error:",
        err
      );


      setError(
        err?.message ||
        "Failed to load security users."
      );


    } finally {

      setLoading(false);

    }

  };


  /* =========================================================
     INITIAL LOAD
  ========================================================= */

  useEffect(() => {

    loadUsers();

  }, []);


  /* =========================================================
     FILTER USERS
  ========================================================= */

  const filteredUsers =
    useMemo(() => {

      const value =
        search
          .toLowerCase()
          .trim();


      if (!value) {
        return [];
      }


      return users.filter(
        (user) => {

          const username =
            String(
              user.username || ""
            )
              .toLowerCase();


          const realName =
            String(
              user.realName || ""
            )
              .toLowerCase();


          const lastName =
            String(
              user.lastName || ""
            )
              .toLowerCase();


          const email =
            String(
              user.email || ""
            )
              .toLowerCase();


          return (
            username.includes(value) ||
            realName.includes(value) ||
            lastName.includes(value) ||
            email.includes(value)
          );

        }
      );

    }, [
      users,
      search,
    ]);


  /* =========================================================
     SECURITY COMPLETE
  ========================================================= */

  const isSecurityComplete = (
    user
  ) => {

    return Boolean(

      String(
        user.realName || ""
      ).trim()

      &&

      String(
        user.lastName || ""
      ).trim()

      &&

      String(
        user.withdrawPassword || ""
      ).trim()

      &&

      String(
        user.securityQuestion || ""
      ).trim()

      &&

      String(
        user.securityAnswer || ""
      ).trim()

      &&

      String(
        user.birthDay || ""
      ).trim()

    );

  };


  /* =========================================================
     LOAD PHONE WALLET
     
     phoneWallet/{username}
  ========================================================= */

  const loadPhoneWallet = async (
    username
  ) => {

    try {

      if (!username) {
        return {};
      }


      const phoneWalletRef =
        doc(
          db,
          "phoneWallet",
          username
        );


      const phoneWalletSnapshot =
        await getDoc(
          phoneWalletRef
        );


      if (
        !phoneWalletSnapshot.exists()
      ) {

        return {};

      }


      return {
        ...phoneWalletSnapshot.data(),
      };


    } catch (err) {

      console.error(
        "Phone wallet loading error:",
        err
      );

      return {};

    }

  };


  /* =========================================================
     SELECT USER
  ========================================================= */

  const handleSelectUser = async (
    user
  ) => {

    try {

      setError("");

      setMessage("");


      /*
       * Load phoneWallet/{username}
       */

      const phoneWallet =
        await loadPhoneWallet(
          user.username || user.id
        );


      /*
       * Combine security + phoneWallet
       */

      const selected =
        {
          ...user,

          phoneWallet,
        };


      setSelectedUser(
        selected
      );


      setForm({

        username:
          user.username || "",

        realName:
          user.realName || "",

        lastName:
          user.lastName || "",

        nickName:
          user.nickName || "",

        withdrawPassword:
          user.withdrawPassword || "",

        securityQuestion:
          user.securityQuestion || "",

        securityAnswer:
          user.securityAnswer || "",

        birthDay:
          user.birthDay || "",

        country:
          user.country || "",

        region:
          user.region || "",

        network:
          user.network || "",

        email:
          user.email || "",

      });


      setPhoneWalletForm({

        network1:
          phoneWallet.network1 || "",

        network2:
          phoneWallet.network2 || "",

        phoneNumber1:
          phoneWallet.phoneNumber1 || "",

        phoneNumber2:
          phoneWallet.phoneNumber2 || "",

        primaryNetworkSelected:
          phoneWallet.primaryNetworkSelected === true,

        primaryPhoneSecured:
          phoneWallet.primaryPhoneSecured === true,

        secondNetworkSelected:
          phoneWallet.secondNetworkSelected === true,

        secondPhoneAdded:
          phoneWallet.secondPhoneAdded === true,

        username:
          phoneWallet.username ||
          user.username ||
          user.id,

        walletSecured:
          phoneWallet.walletSecured === true,

      });


      setEditMode(false);

      setPhoneWalletEditMode(false);

      setShowPassword(false);

      setShowAnswer(false);

      setEditingWalletIndex(null);

      setWalletForm({});


    } catch (err) {

      console.error(
        "Select user error:",
        err
      );


      setError(
        err?.message ||
        "Failed to load user information."
      );

    }

  };


  /* =========================================================
     CLOSE USER
  ========================================================= */

  const closeUser = () => {

    if (saving) {
      return;
    }


    setSelectedUser(null);

    setEditMode(false);

    setPhoneWalletEditMode(false);

    setEditingWalletIndex(null);

    setWalletForm({});

    setPhoneWalletForm({});

    setMessage("");

    setError("");

  };


  /* =========================================================
     FORM CHANGE
  ========================================================= */

  const handleChange = (
    event
  ) => {

    const {
      name,
      value,
    } = event.target;


    setForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );

  };


  /* =========================================================
     PHONE WALLET CHANGE
  ========================================================= */

  const handlePhoneWalletChange = (
    event
  ) => {

    const {
      name,
      value,
      type,
      checked,
    } = event.target;


    setPhoneWalletForm(
      (previous) => ({
        ...previous,
        [name]:
          type === "checkbox"
            ? checked
            : value,
      })
    );

  };


  /* =========================================================
     SAVE USER
  ========================================================= */

  const handleSave = async () => {

    if (!selectedUser) {
      return;
    }


    try {

      setSaving(true);

      setError("");

      setMessage("");


      const username =
        selectedUser.id;


      const securityRef =
        doc(
          db,
          "security",
          username
        );


      const updatedData = {

        username:
          form.username ||
          username,

        realName:
          form.realName || "",

        lastName:
          form.lastName || "",

        nickName:
          form.nickName || "",

        withdrawPassword:
          form.withdrawPassword || "",

        securityQuestion:
          form.securityQuestion || "",

        securityAnswer:
          form.securityAnswer || "",

        birthDay:
          form.birthDay || "",

        country:
          form.country || "",

        region:
          form.region || "",

        network:
          form.network || "",

        email:
          form.email || "",

        wallets:
          Array.isArray(
            selectedUser.wallets
          )
            ? selectedUser.wallets
            : [],

        updatedAt:
          new Date(),

      };


      await setDoc(
        securityRef,
        updatedData,
        {
          merge: true,
        }
      );


      const updatedUser = {

        ...selectedUser,

        ...updatedData,

      };


      setSelectedUser(
        updatedUser
      );


      setUsers(
        (previous) =>
          previous.map(
            (user) =>
              user.id === username
                ? updatedUser
                : user
          )
      );


      setEditMode(false);


      setMessage(
        "User security information saved successfully."
      );


    } catch (err) {

      console.error(
        "Admin save error:",
        err
      );


      setError(
        err?.message ||
        "Failed to save user information."
      );


    } finally {

      setSaving(false);

    }

  };


  /* =========================================================
     SAVE PHONE WALLET
  ========================================================= */

  const savePhoneWallet = async () => {

    if (!selectedUser) {
      return;
    }


    try {

      setSaving(true);

      setError("");

      setMessage("");


      const username =
        selectedUser.username ||
        selectedUser.id;


      const phoneWalletRef =
        doc(
          db,
          "phoneWallet",
          username
        );


      const phoneWalletData = {

        network1:
          phoneWalletForm.network1 || "",

        network2:
          phoneWalletForm.network2 || "",

        phoneNumber1:
          phoneWalletForm.phoneNumber1 || "",

        phoneNumber2:
          phoneWalletForm.phoneNumber2 || "",

        primaryNetworkSelected:
          phoneWalletForm.primaryNetworkSelected === true,

        primaryPhoneSecured:
          phoneWalletForm.primaryPhoneSecured === true,

        secondNetworkSelected:
          phoneWalletForm.secondNetworkSelected === true,

        secondPhoneAdded:
          phoneWalletForm.secondPhoneAdded === true,

        username,

        walletSecured:
          phoneWalletForm.walletSecured === true,

        updatedAt:
          new Date(),

      };


      await setDoc(
        phoneWalletRef,
        phoneWalletData,
        {
          merge: true,
        }
      );


      setSelectedUser(
        (previous) => ({
          ...previous,
          phoneWallet:
            phoneWalletData,
        })
      );


      setPhoneWalletEditMode(
        false
      );


      setMessage(
        "Phone wallet information saved successfully."
      );


    } catch (err) {

      console.error(
        "Phone wallet save error:",
        err
      );


      setError(
        err?.message ||
        "Failed to save phone wallet."
      );


    } finally {

      setSaving(false);

    }

  };


  /* =========================================================
     START WALLET EDIT
  ========================================================= */

  const startWalletEdit = (
    wallet,
    index
  ) => {

    setEditingWalletIndex(
      index
    );


    setWalletForm({

      country:
        wallet.country || "",

      code:
        wallet.code || "",

      currency:
        wallet.currency || "",

      network:
        wallet.network || "",

      number:
        wallet.number || "",

      type:
        wallet.type ||
        "other-country",

      walletSecured:
        wallet.walletSecured !== false,

    });

  };


  /* =========================================================
     CANCEL WALLET EDIT
  ========================================================= */

  const cancelWalletEdit = () => {

    setEditingWalletIndex(
      null
    );

    setWalletForm({});

  };


  /* =========================================================
     WALLET CHANGE
  ========================================================= */

  const handleWalletChange = (
    event
  ) => {

    const {
      name,
      value,
    } = event.target;


    setWalletForm(
      (previous) => ({
        ...previous,
        [name]: value,
      })
    );

  };


  /* =========================================================
     SAVE WALLET
  ========================================================= */

  const saveWallet = async () => {

    if (
      !selectedUser ||
      editingWalletIndex === null
    ) {

      return;

    }


    try {

      setSaving(true);

      setError("");

      setMessage("");


      const currentWallets =
        Array.isArray(
          selectedUser.wallets
        )
          ? [
              ...selectedUser.wallets,
            ]
          : [];


      currentWallets[
        editingWalletIndex
      ] = {

        ...currentWallets[
          editingWalletIndex
        ],

        country:
          walletForm.country || "",

        code:
          walletForm.code || "",

        currency:
          walletForm.currency || "",

        network:
          walletForm.network || "",

        number:
          walletForm.number || "",

        type:
          walletForm.type ||
          "other-country",

        walletSecured:
          walletForm.walletSecured !== false,

      };


      await setDoc(
        doc(
          db,
          "security",
          selectedUser.id
        ),
        {

          wallets:
            currentWallets,

          updatedAt:
            new Date(),

        },
        {
          merge: true,
        }
      );


      const updatedUser = {

        ...selectedUser,

        wallets:
          currentWallets,

      };


      setSelectedUser(
        updatedUser
      );


      setUsers(
        (previous) =>
          previous.map(
            (user) =>
              user.id ===
              selectedUser.id
                ? updatedUser
                : user
          )
      );


      setEditingWalletIndex(
        null
      );

      setWalletForm({});


      setMessage(
        "Wallet updated successfully."
      );


    } catch (err) {

      console.error(
        "Wallet update error:",
        err
      );


      setError(
        err?.message ||
        "Failed to update wallet."
      );


    } finally {

      setSaving(false);

    }

  };


  /* =========================================================
     DELETE WALLET
  ========================================================= */

  const deleteWallet = async (
    index
  ) => {

    if (!selectedUser) {
      return;
    }


    const confirmed =
      window.confirm(
        "Are you sure you want to delete this wallet?"
      );


    if (!confirmed) {
      return;
    }


    try {

      setSaving(true);

      setError("");

      setMessage("");


      const currentWallets =
        Array.isArray(
          selectedUser.wallets
        )
          ? [
              ...selectedUser.wallets,
            ]
          : [];


      currentWallets.splice(
        index,
        1
      );


      await setDoc(
        doc(
          db,
          "security",
          selectedUser.id
        ),
        {

          wallets:
            currentWallets,

          updatedAt:
            new Date(),

        },
        {
          merge: true,
        }
      );


      const updatedUser = {

        ...selectedUser,

        wallets:
          currentWallets,

      };


      setSelectedUser(
        updatedUser
      );


      setUsers(
        (previous) =>
          previous.map(
            (user) =>
              user.id ===
              selectedUser.id
                ? updatedUser
                : user
          )
      );


      setMessage(
        "Wallet deleted successfully."
      );


    } catch (err) {

      console.error(
        "Wallet delete error:",
        err
      );


      setError(
        err?.message ||
        "Failed to delete wallet."
      );


    } finally {

      setSaving(false);

    }

  };


  /* =========================================================
     DELETE SECURITY USER
  ========================================================= */

  const handleDeleteUser = async () => {

    if (!selectedUser) {
      return;
    }


    try {

      setSaving(true);

      setError("");

      setMessage("");


      await deleteDoc(
        doc(
          db,
          "security",
          selectedUser.id
        )
      );


      setUsers(
        (previous) =>
          previous.filter(
            (user) =>
              user.id !==
              selectedUser.id
          )
      );


      setSelectedUser(
        null
      );


      setDeleteConfirm(
        false
      );


      setMessage(
        "Security record deleted successfully."
      );


    } catch (err) {

      console.error(
        "Delete security user error:",
        err
      );


      setError(
        err?.message ||
        "Failed to delete security record."
      );


    } finally {

      setSaving(false);

    }

  };


  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {

    return (

      <div className="admin-security-page">

        <div className="admin-security-loading">

          <FaSpinner
            className="admin-spinner"
          />

          <strong>
            Loading security records...
          </strong>

          <span>
            Please wait...
          </span>

        </div>

      </div>

    );

  }


  /* =========================================================
     PHONE WALLET DATA
  ========================================================= */

  const phoneWallet =
    selectedUser?.phoneWallet || {};


  /* =========================================================
     RETURN
  ========================================================= */

  return (

    <div className="admin-security-page">


      {/* =====================================================
          HEADER
      ===================================================== */}

      <header className="admin-security-header">

        <div>

          <span className="admin-eyebrow">
            ADMIN PANEL
          </span>

          <h1>
            Security Management
          </h1>

          <p>
            Search and manage user security,
            wallets and phone wallet information.
          </p>

        </div>


        <div className="admin-user-count">

          <strong>
            {users.length}
          </strong>

          <span>
            Security Records
          </span>

        </div>

      </header>


      {/* =====================================================
          MESSAGE
      ===================================================== */}

      {message && (

        <div className="admin-success">

          <FaCheckCircle />

          <span>
            {message}
          </span>

          <button
            type="button"
            onClick={() =>
              setMessage("")
            }
          >
            ×
          </button>

        </div>

      )}


      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (

        <div className="admin-error">

          <span>
            !
          </span>

          <span>
            {error}
          </span>

          <button
            type="button"
            onClick={() =>
              setError("")
            }
          >
            ×
          </button>

        </div>

      )}


      {/* =====================================================
          SEARCH
      ===================================================== */}

      <div className="admin-search-box">

        <FaSearch />

        <input
          type="text"
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Type username, name or email..."
        />

        {search.trim() && (

          <span>
            {filteredUsers.length}
          </span>

        )}

      </div>


      <div className="admin-security-layout">


        {/* ===================================================
            USERS LIST
        =================================================== */}

        <section className="admin-users-panel">

          <div className="admin-panel-title">

            <div>

              <h2>
                Users
              </h2>

              <span>
                Search results
              </span>

            </div>

          </div>


          {!search.trim() ? (

            <div className="admin-empty">

              <FaSearch />

              <strong>
                Search for a user
              </strong>

              <span>
                Start typing a username, name or email
                to find a security record.
              </span>

            </div>

          ) : filteredUsers.length === 0 ? (

            <div className="admin-empty">

              <FaUser />

              <strong>
                No users found
              </strong>

              <span>
                No security record matches your search.
              </span>

            </div>

          ) : (

            <div className="admin-users-list">

              {filteredUsers.map(
                (user) => {

                  const complete =
                    isSecurityComplete(
                      user
                    );

                  const selected =
                    selectedUser?.id ===
                    user.id;


                  return (

                    <button
                      type="button"
                      key={user.id}
                      className={
                        selected
                          ? "admin-user-card selected"
                          : "admin-user-card"
                      }
                      onClick={() =>
                        handleSelectUser(
                          user
                        )
                      }
                    >

                      <div className="admin-user-avatar">

                        <FaUser />

                      </div>


                      <div className="admin-user-info">

                        <strong>
                          {user.username ||
                            user.id}
                        </strong>

                        <span>

                          {user.realName ||
                            "No name"}

                          {" "}

                          {user.lastName ||
                            ""}

                        </span>

                        <small>

                          {user.email ||
                            "No email"}

                        </small>

                      </div>


                      <div className="admin-user-status">

                        {complete ? (

                          <FaCheckCircle />

                        ) : (

                          <FaLock />

                        )}

                        <FaChevronRight />

                      </div>

                    </button>

                  );

                }
              )}

            </div>

          )}

        </section>


        {/* ===================================================
            DETAILS
        =================================================== */}

        <section className="admin-details-panel">

          {!selectedUser ? (

            <div className="admin-no-selection">

              <FaShieldAlt />

              <h2>
                Select a user
              </h2>

              <p>
                Search for a user above, then select
                the matching security record.
              </p>

            </div>

          ) : (

            <>


              {/* =============================================
                  DETAILS HEADER
              ============================================= */}

              <div className="admin-details-header">

                <div>

                  <span>
                    SECURITY RECORD
                  </span>

                  <h2>
                    {selectedUser.username ||
                      selectedUser.id}
                  </h2>

                  <p>

                    {selectedUser.realName ||
                      "No name"}

                    {" "}

                    {selectedUser.lastName ||
                      ""}

                  </p>

                </div>


                <div className="admin-detail-actions">

                  {!editMode ? (

                    <button
                      type="button"
                      className="admin-edit-button"
                      onClick={() =>
                        setEditMode(true)
                      }
                    >

                      <FaEdit />

                      Edit

                    </button>

                  ) : (

                    <button
                      type="button"
                      className="admin-save-button"
                      onClick={
                        handleSave
                      }
                      disabled={
                        saving
                      }
                    >

                      {saving ? (

                        <FaSpinner
                          className="admin-spinner"
                        />

                      ) : (

                        <FaSave />

                      )}

                      Save

                    </button>

                  )}


                  <button
                    type="button"
                    className="admin-close-button"
                    onClick={
                      closeUser
                    }
                  >

                    <FaTimes />

                  </button>

                </div>

              </div>


              {/* =============================================
                  PERSONAL INFORMATION
              ============================================= */}

              <div className="admin-section">

                <div className="admin-section-heading">

                  <FaUser />

                  <div>

                    <h3>
                      Personal Information
                    </h3>

                    <span>
                      User profile details
                    </span>

                  </div>

                </div>


                <div className="admin-form-grid">

                  <div className="admin-field">

                    <label>
                      Username
                    </label>

                    <input
                      name="username"
                      value={
                        form.username || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Real Name
                    </label>

                    <input
                      name="realName"
                      value={
                        form.realName || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Last Name
                    </label>

                    <input
                      name="lastName"
                      value={
                        form.lastName || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Nickname
                    </label>

                    <input
                      name="nickName"
                      value={
                        form.nickName || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Birthday
                    </label>

                    <input
                      type="date"
                      name="birthDay"
                      value={
                        form.birthDay || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Country
                    </label>

                    <input
                      name="country"
                      value={
                        form.country || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Region
                    </label>

                    <input
                      name="region"
                      value={
                        form.region || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Network
                    </label>

                    <input
                      name="network"
                      value={
                        form.network || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field full">

                    <label>
                      Email Address
                    </label>

                    <input
                      type="email"
                      name="email"
                      value={
                        form.email || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>

                </div>

              </div>


              {/* =============================================
                  SECURITY
              ============================================= */}

              <div className="admin-section">

                <div className="admin-section-heading">

                  <FaShieldAlt />

                  <div>

                    <h3>
                      Security Information
                    </h3>

                    <span>
                      Security verification status
                    </span>

                  </div>

                </div>


                <div className="admin-form-grid">

                  <div className="admin-field">

                    <label>
                      Security Question
                    </label>

                    <input
                      name="securityQuestion"
                      value={
                        form.securityQuestion || ""
                      }
                      onChange={
                        handleChange
                      }
                      disabled={
                        !editMode
                      }
                    />

                  </div>


                  <div className="admin-field">

                    <label>
                      Security Answer
                    </label>

                    <div className="admin-secret-input">

                      <input
                        type={
                          showAnswer
                            ? "text"
                            : "password"
                        }
                        name="securityAnswer"
                        value={
                          form.securityAnswer || ""
                        }
                        onChange={
                          handleChange
                        }
                        disabled={
                          !editMode
                        }
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowAnswer(
                            !showAnswer
                          )
                        }
                      >

                        {showAnswer
                          ? <FaEyeSlash />
                          : <FaEye />
                        }

                      </button>

                    </div>

                  </div>


                  <div className="admin-field">

                    <label>
                      Withdrawal Password
                    </label>

                    <div className="admin-secret-input">

                      <input
                        type={
                          showPassword
                            ? "text"
                            : "password"
                        }
                        name="withdrawPassword"
                        value={
                          form.withdrawPassword || ""
                        }
                        onChange={
                          handleChange
                        }
                        disabled={
                          !editMode
                        }
                      />

                      <button
                        type="button"
                        onClick={() =>
                          setShowPassword(
                            !showPassword
                          )
                        }
                      >

                        {showPassword
                          ? <FaEyeSlash />
                          : <FaEye />
                        }

                      </button>

                    </div>

                  </div>


                  <div className="admin-security-status-box">

                    {isSecurityComplete(
                      selectedUser
                    ) ? (

                      <>

                        <FaCheckCircle />

                        <div>

                          <strong>
                            Security Complete
                          </strong>

                          <span>
                            Required security information
                            is available.
                          </span>

                        </div>

                      </>

                    ) : (

                      <>

                        <FaLock />

                        <div>

                          <strong>
                            Security Incomplete
                          </strong>

                          <span>
                            Some required information is missing.
                          </span>

                        </div>

                      </>

                    )}

                  </div>

                </div>

              </div>


              {/* =============================================
                  PHONE WALLET
              ============================================= */}

              <div className="admin-section">

                <div className="admin-section-heading">

                  <FaMobileAlt />

                  <div>

                    <h3>
                      Phone Wallet
                    </h3>

                    <span>
                      phoneWallet/{selectedUser.username || selectedUser.id}
                    </span>

                  </div>

                </div>


                <div className="admin-phone-wallet-card">

                  <div className="admin-phone-wallet-header">

                    <div>

                      <strong>
                        Phone Wallet Information
                      </strong>

                      <span>
                        Mobile wallet configuration
                      </span>

                    </div>


                    {!phoneWalletEditMode ? (

                      <button
                        type="button"
                        className="admin-edit-button"
                        onClick={() =>
                          setPhoneWalletEditMode(
                            true
                          )
                        }
                      >

                        <FaEdit />

                        Edit

                      </button>

                    ) : (

                      <div className="admin-phone-wallet-buttons">

                        <button
                          type="button"
                          className="admin-save-button"
                          onClick={
                            savePhoneWallet
                          }
                          disabled={
                            saving
                          }
                        >

                          {saving ? (
                            <FaSpinner
                              className="admin-spinner"
                            />
                          ) : (
                            <FaSave />
                          )}

                          Save

                        </button>


                        <button
                          type="button"
                          className="admin-cancel-button"
                          onClick={() => {

                            setPhoneWalletEditMode(
                              false
                            );

                            setPhoneWalletForm({

                              network1:
                                phoneWallet.network1 || "",

                              network2:
                                phoneWallet.network2 || "",

                              phoneNumber1:
                                phoneWallet.phoneNumber1 || "",

                              phoneNumber2:
                                phoneWallet.phoneNumber2 || "",

                              primaryNetworkSelected:
                                phoneWallet.primaryNetworkSelected === true,

                              primaryPhoneSecured:
                                phoneWallet.primaryPhoneSecured === true,

                              secondNetworkSelected:
                                phoneWallet.secondNetworkSelected === true,

                              secondPhoneAdded:
                                phoneWallet.secondPhoneAdded === true,

                              username:
                                phoneWallet.username ||
                                selectedUser.username ||
                                selectedUser.id,

                              walletSecured:
                                phoneWallet.walletSecured === true,

                            });

                          }}
                          disabled={
                            saving
                          }
                        >

                          Cancel

                        </button>

                      </div>

                    )}

                  </div>


                  {phoneWalletEditMode ? (

                    <div className="admin-form-grid">


                      <div className="admin-field">

                        <label>
                          Network 1
                        </label>

                        <input
                          name="network1"
                          value={
                            phoneWalletForm.network1 ||
                            ""
                          }
                          onChange={
                            handlePhoneWalletChange
                          }
                        />

                      </div>


                      <div className="admin-field">

                        <label>
                          Network 2
                        </label>

                        <input
                          name="network2"
                          value={
                            phoneWalletForm.network2 ||
                            ""
                          }
                          onChange={
                            handlePhoneWalletChange
                          }
                        />

                      </div
                      >


<div className="admin-field">

  <label>
    Phone Number 1
  </label>

  <input
    type="tel"
    name="phoneNumber1"
    value={
      phoneWalletForm.phoneNumber1 ||
      ""
    }
    onChange={
      handlePhoneWalletChange
    }
  />

</div>


<div className="admin-field">

  <label>
    Phone Number 2
  </label>

  <input
    type="tel"
    name="phoneNumber2"
    value={
      phoneWalletForm.phoneNumber2 ||
      ""
    }
    onChange={
      handlePhoneWalletChange
    }
  />

</div>


<div className="admin-field">

  <label>
    Username
  </label>

  <input
    name="username"
    value={
      phoneWalletForm.username ||
      ""
    }
    disabled
  />

</div>


<div className="admin-field">

  <label>
    Wallet Secured
  </label>

  <label className="admin-checkbox">

    <input
      type="checkbox"
      name="walletSecured"
      checked={
        phoneWalletForm.walletSecured === true
      }
      onChange={
        handlePhoneWalletChange
      }
    />

    <span>
      Wallet secured
    </span>

  </label>

</div>


<div className="admin-field">

  <label>
    Primary Network Selected
  </label>

  <label className="admin-checkbox">

    <input
      type="checkbox"
      name="primaryNetworkSelected"
      checked={
        phoneWalletForm.primaryNetworkSelected === true
      }
      onChange={
        handlePhoneWalletChange
      }
    />

    <span>
      Selected
    </span>

  </label>

</div>


<div className="admin-field">

  <label>
    Primary Phone Secured
  </label>

  <label className="admin-checkbox">

    <input
      type="checkbox"
      name="primaryPhoneSecured"
      checked={
        phoneWalletForm.primaryPhoneSecured === true
      }
      onChange={
        handlePhoneWalletChange
      }
    />

    <span>
      Secured
    </span>

  </label>

</div>


<div className="admin-field">

  <label>
    Second Network Selected
  </label>

  <label className="admin-checkbox">

    <input
      type="checkbox"
      name="secondNetworkSelected"
      checked={
        phoneWalletForm.secondNetworkSelected === true
      }
      onChange={
        handlePhoneWalletChange
      }
    />

    <span>
      Selected
    </span>

  </label>

</div>


<div className="admin-field">

  <label>
    Second Phone Added
  </label>

  <label className="admin-checkbox">

    <input
      type="checkbox"
      name="secondPhoneAdded"
      checked={
        phoneWalletForm.secondPhoneAdded === true
      }
      onChange={
        handlePhoneWalletChange
      }
    />

    <span>
      Added
    </span>

  </label>

</div>

</div>

) : (

<div className="admin-phone-wallet-grid">


<div className="admin-phone-wallet-item">

  <span>
    Network 1
  </span>

  <strong>
    {phoneWallet.network1 ||
      "Not available"}
  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Phone Number 1
  </span>

  <strong>
    {maskPhone(
      phoneWallet.phoneNumber1
    )}
  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Network 2
  </span>

  <strong>
    {phoneWallet.network2 ||
      "Not available"}
  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Phone Number 2
  </span>

  <strong>
    {maskPhone(
      phoneWallet.phoneNumber2
    )}
  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Primary Network
  </span>

  <strong>

    {phoneWallet.primaryNetworkSelected
      ? "Selected"
      : "Not selected"}

  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Primary Phone
  </span>

  <strong>

    {phoneWallet.primaryPhoneSecured
      ? "Secured"
      : "Not secured"}

  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Second Network
  </span>

  <strong>

    {phoneWallet.secondNetworkSelected
      ? "Selected"
      : "Not selected"}

  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Second Phone
  </span>

  <strong>

    {phoneWallet.secondPhoneAdded
      ? "Added"
      : "Not added"}

  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Wallet Status
  </span>

  <strong>

    {phoneWallet.walletSecured
      ? "Secured"
      : "Not secured"}

  </strong>

</div>


<div className="admin-phone-wallet-item">

  <span>
    Username
  </span>

  <strong>
    {phoneWallet.username ||
      selectedUser.username ||
      selectedUser.id}
  </strong>

</div>

</div>

)}

</div>

</div>


{/* =============================================
USER WALLETS
============================================= */}

<div className="admin-section">

<div className="admin-section-heading">

<FaWallet />

<div>

<h3>
User Wallets
</h3>

<span>

{Array.isArray(
  selectedUser.wallets
)
  ? selectedUser.wallets.length
  : 0}

{" "}saved wallet(s)

</span>

</div>

</div>


{!Array.isArray(
selectedUser.wallets
) ||
selectedUser.wallets.length === 0 ? (

<div className="admin-wallet-empty">

<FaWallet />

<span>
No wallets found.
</span>

</div>

) : (

<div className="admin-wallet-list">

{selectedUser.wallets.map(
(
  wallet,
  index
) => {

  const editing =
    editingWalletIndex ===
    index;


  return (

    <div
      className="admin-wallet-card"
      key={
        `${wallet.country || "wallet"}-${index}`
      }
    >

      {editing ? (

        <div className="admin-wallet-edit">

          <div className="admin-form-grid">

            <div className="admin-field">

              <label>
                Country
              </label>

              <input
                name="country"
                value={
                  walletForm.country ||
                  ""
                }
                onChange={
                  handleWalletChange
                }
              />

            </div>


            <div className="admin-field">

              <label>
                Currency
              </label>

              <input
                name="currency"
                value={
                  walletForm.currency ||
                  ""
                }
                onChange={
                  handleWalletChange
                }
              />

            </div>


            <div className="admin-field">

              <label>
                Country Code
              </label>

              <input
                name="code"
                value={
                  walletForm.code ||
                  ""
                }
                onChange={
                  handleWalletChange
                }
              />

            </div>


            <div className="admin-field">

              <label>
                Network
              </label>

              <input
                name="network"
                value={
                  walletForm.network ||
                  ""
                }
                onChange={
                  handleWalletChange
                }
              />

            </div>


            <div className="admin-field full">

              <label>
                Wallet Number
              </label>

              <input
                name="number"
                value={
                  walletForm.number ||
                  ""
                }
                onChange={
                  handleWalletChange
                }
              />

            </div>

          </div>


          <div className="admin-wallet-actions">

            <button
              type="button"
              className="admin-save-button"
              onClick={
                saveWallet
              }
              disabled={
                saving
              }
            >

              {saving ? (
                <FaSpinner
                  className="admin-spinner"
                />
              ) : (
                <FaSave />
              )}

              Save Wallet

            </button>


            <button
              type="button"
              className="admin-cancel-button"
              onClick={
                cancelWalletEdit
              }
              disabled={
                saving
              }
            >

              Cancel

            </button>

          </div>

        </div>

      ) : (

        <>

          <div className="admin-wallet-icon">

            {wallet.country ===
              "Kenya"
              ? "🇰🇪"
              : wallet.country ===
                "Tanzania"
              ? "🇹🇿"
              : "🌍"}

          </div>


          <div className="admin-wallet-content">

            <div className="admin-wallet-title">

              <strong>
                {wallet.country ||
                  "Unknown Country"}
              </strong>

              <span>
                {wallet.currency ||
                  ""}
              </span>

            </div>


            <p>
              {wallet.network ||
                "No network"}
            </p>


            <small>

              {wallet.code ||
                ""}

              {" "}

              {maskPhone(
                wallet.number
              )}

            </small>


            <div className="admin-wallet-secured">

              {wallet.walletSecured ? (
                <>
                  <FaCheckCircle />
                  Secured
                </>
              ) : (
                <>
                  <FaLock />
                  Not secured
                </>
              )}

            </div>

          </div>


          <div className="admin-wallet-actions">

            <button
              type="button"
              className="admin-wallet-edit-button"
              onClick={() =>
                startWalletEdit(
                  wallet,
                  index
                )
              }
            >

              <FaEdit />

              Edit

            </button>


            <button
              type="button"
              className="admin-wallet-delete-button"
              onClick={() =>
                deleteWallet(
                  index
                )
              }
              disabled={
                saving
              }
            >

              <FaTrash />

            </button>

          </div>

        </>

      )}

    </div>

  );

}
)}

</div>

)}

</div>


{/* =============================================
DELETE USER
============================================= */}

<div className="admin-danger-zone">

<div>

<FaTrash />

<div>

<h3>
Delete Security Record
</h3>

<p>
This permanently deletes the user's
security document.
</p>

</div>

</div>


<button
type="button"
onClick={() =>
setDeleteConfirm(true)
}
disabled={
saving
}
>

<FaTrash />

Delete Record

</button>

</div>

</>

)}

</section>

</div>


{/* =====================================================
DELETE CONFIRM MODAL
===================================================== */}

{deleteConfirm &&
selectedUser && (

<div className="admin-confirm-overlay">

<div className="admin-confirm-modal">

<div className="admin-confirm-icon">

<FaTrash />

</div>


<h2>
Delete Security Record?
</h2>


<p>

You are about to delete the security
record for{" "}

<strong>
{selectedUser.username ||
selectedUser.id}
</strong>.

</p>


<p className="admin-confirm-warning">

This action cannot be undone.

</p>


<div className="admin-confirm-actions">

<button
type="button"
className="admin-cancel-button"
onClick={() =>
setDeleteConfirm(false)
}
disabled={
saving
}
>

Cancel

</button>


<button
type="button"
className="admin-danger-button"
onClick={
handleDeleteUser
}
disabled={
saving
}
>

{saving
? "Deleting..."
: "Yes, Delete"}

</button>

</div>

</div>

</div>

)}

</div>

);

}